import {
	createWriteStream,
	existsSync,
	mkdirSync,
	statSync,
	truncateSync,
	unlinkSync,
} from "node:fs";
import { tmpdir } from "node:os";
import { join } from "node:path";
import type { LogEvent } from "@better-ccflare/types";

// Local constants to avoid circular dependency with core
const BUFFER_SIZES = {
	LOG_FILE_MAX_SIZE: 10 * 1024 * 1024, // 10MB
} as const;

const LIMITS = {
	LOG_MESSAGE_MAX_LENGTH: 10000,
	LOG_READ_DEFAULT: 1000,
} as const;

// Simple disposable interface to avoid circular dependency
interface Disposable {
	dispose(): void;
}

const disposables = new Set<Disposable>();

function registerDisposable(disposable: Disposable): void {
	disposables.add(disposable);
}

// String(e) can itself throw (e.g. an object with a throwing
// Symbol.toPrimitive, or Object.create(null)), which would defeat the
// purpose of the catch block calling this. Never let this throw.
function safeErrorReason(e: unknown): string {
	if (e instanceof Error) return e.message;
	try {
		return String(e);
	} catch {
		return "unknown error";
	}
}

export class LogFileWriter implements Disposable {
	private logDir: string;
	private logFile: string;
	private stream: ReturnType<typeof createWriteStream> | null = null;
	private maxFileSize = BUFFER_SIZES.LOG_FILE_MAX_SIZE;
	private writeCount = 0;
	private static readonly SIZE_CHECK_INTERVAL = 100;

	constructor() {
		// Use environment variable if set, otherwise use tmp folder
		this.logDir =
			process.env.BETTER_CCFLARE_LOG_DIR ||
			join(tmpdir(), "better-ccflare-logs");
		if (!existsSync(this.logDir)) {
			mkdirSync(this.logDir, { recursive: true });
		}

		this.logFile = join(this.logDir, "app.log");
		this.initStream();
	}

	private initStream(): void {
		// Close existing stream if any
		if (this.stream && !this.stream.destroyed) {
			this.stream.end();
			this.stream = null;
		}

		// Check if we need to rotate
		if (existsSync(this.logFile)) {
			const stats = statSync(this.logFile);
			if (stats.size > this.maxFileSize) {
				this.rotateLog();
			}
		}

		// Create write stream with append mode
		this.stream = createWriteStream(this.logFile, { flags: "a" });
	}

	private rotateLog(): void {
		if (this.stream) {
			this.stream.end();
			this.stream = null;
		}

		if (existsSync(this.logFile)) {
			try {
				unlinkSync(this.logFile);
			} catch (e: unknown) {
				const code = (e as NodeJS.ErrnoException).code;
				if (code === "EACCES" || code === "EPERM") {
					// Fallback: try truncating the file instead
					try {
						truncateSync(this.logFile, 0);
					} catch (truncErr) {
						console.error(
							"Log rotation fallback to truncate failed, switching to new file:",
							truncErr,
						);
						// Last resort: switch to a timestamped log file
						this.logFile = join(this.logDir, `app-${Date.now()}.log`);
					}
				} else {
					console.error("Failed to rotate log:", e);
				}
			}
		}
	}

	write(event: LogEvent): void {
		if (!this.stream || this.stream.destroyed) {
			this.initStream();
		}

		// Periodic size check to trigger rotation mid-stream (every N writes)
		if (++this.writeCount % LogFileWriter.SIZE_CHECK_INTERVAL === 0) {
			try {
				if (existsSync(this.logFile)) {
					const stats = statSync(this.logFile);
					if (stats.size > this.maxFileSize) {
						this.rotateLog();
						this.initStream();
					}
				}
			} catch {
				// Ignore stat errors, will be caught on next initStream
			}
		}

		let line: string;
		try {
			line = `${JSON.stringify(event)}\n`;
		} catch (e: unknown) {
			// event.data comes from caller-supplied log payloads and may contain
			// circular references or BigInt values, both of which make
			// JSON.stringify throw. A logging call must never crash its caller,
			// so fall back to a sanitized line that preserves ts/level/msg and
			// replaces only the offending data field.
			const reason = safeErrorReason(e);
			const fallback: LogEvent = {
				ts: event.ts,
				level: event.level,
				msg: event.msg,
				data: `[unserializable: ${reason}]`,
			};
			line = `${JSON.stringify(fallback)}\n`;
		}
		if (this.stream) {
			this.stream.write(line);
		}
	}

	async readLogs(limit: number = LIMITS.LOG_READ_DEFAULT): Promise<LogEvent[]> {
		if (!existsSync(this.logFile)) {
			return [];
		}

		try {
			const lines = await this.readTailLines(limit);

			// Return the last N logs
			return lines
				.slice(-limit)
				.map((line) => {
					try {
						return JSON.parse(line);
					} catch {
						return null;
					}
				})
				.filter((log): log is LogEvent => log !== null);
		} catch (_e) {
			console.error("Failed to read logs:", _e);
			return [];
		}
	}

	// Reads only as much of the file's tail as needed to yield `limit`
	// non-empty lines, doubling the window until enough lines are found.
	private async readTailLines(limit: number): Promise<string[]> {
		const file = Bun.file(this.logFile);
		const size = file.size;
		let window = 256 * 1024;
		while (true) {
			const start = Math.max(0, size - window);
			const text = await file.slice(start, size).text();
			const lines = text.split("\n");
			// A window starting mid-file begins with a partial line; drop it.
			if (start > 0) lines.shift();
			const nonEmpty = lines.filter((l) => l.trim().length > 0);
			if (start === 0 || nonEmpty.length > limit) {
				return nonEmpty;
			}
			window *= 2;
		}
	}

	close(): void {
		if (this.stream) {
			this.stream.end();
			this.stream = null;
		}
	}

	dispose(): void {
		this.close();
	}
}

// Check if we're in a Node.js/Bun environment (not browser)
const isNodeEnvironment =
	typeof process !== "undefined" &&
	process.versions != null &&
	process.versions.node != null;

// Singleton instance - only create in Node.js environments
export const logFileWriter: LogFileWriter | null = isNodeEnvironment
	? new LogFileWriter()
	: null;

// Register with lifecycle manager (only in Node.js)
if (logFileWriter) {
	registerDisposable(logFileWriter);
}
